import * as React from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader, InfoIcon, Mail } from "lucide-react";
import { InputGroup, InputGroupInput, InputGroupAddon } from "@/taskpane/components/ui/input-group";
import { Button } from "@/taskpane/components/ui/button";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/taskpane/components/ui/field";
import { useSendResetPasswordEmail } from "@/hooks/useSendResetPasswordEmail";
import OnboardingHeader from "../OnboardingHeader";

interface ForgotPasswordEmailProps {
  onBackToLogin: () => void;
  onEmailSent: (email: string) => void;
}

const forgotPasswordEmailSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
});

type ForgotPasswordEmailFormData = z.infer<typeof forgotPasswordEmailSchema>;

const ForgotPasswordEmail: React.FC<ForgotPasswordEmailProps> = ({
  onBackToLogin,
  onEmailSent,
}) => {
  const sendResetEmailMutation = useSendResetPasswordEmail();

  const {
    control,
    handleSubmit,
    formState: { },
  } = useForm<ForgotPasswordEmailFormData>({
    resolver: zodResolver(forgotPasswordEmailSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordEmailFormData) => {
    try {
      await sendResetEmailMutation.mutateAsync({ email: data.email });
      onEmailSent(data.email);
    } catch (error) {
      console.error("Failed to send reset password email:", error);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-md mx-auto">
      <OnboardingHeader onBack={onBackToLogin} />

      <section
        className="flex flex-col items-center gap-6 w-full"
        role="main"
        aria-labelledby="forgot-password-heading"
        aria-describedby="forgot-password-description"
      >
        <div className="text-center">
          <h2 id="forgot-password-heading" className="text-xl font-medium m-0 mb-2">
            Forgot Password
          </h2>
          <p
            id="forgot-password-description"
            className="text-sm text-neutral-400 m-0 leading-normal"
          >
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 w-full"
          noValidate
          aria-label="Forgot password form"
        >
          <FieldGroup>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="gap-1">
                  <FieldLabel className="font-medium text-sm leading-5 text-neutral-400">
                    Email
                  </FieldLabel>
                  <FieldContent>
                    <InputGroup aria-invalid={fieldState.invalid}>
                      <InputGroupAddon>
                        <Mail className="text-neutral-200" />
                      </InputGroupAddon>
                      <InputGroupInput
                        type="email"
                        placeholder="Enter your email"
                        {...field}
                        disabled={sendResetEmailMutation.isPending}
                        aria-describedby={fieldState.invalid ? "email-error" : undefined}
                        aria-invalid={fieldState.invalid}
                        autoComplete="email"
                        className="placeholder:text-neutral-500 text-sm py-3 px-4"
                      />
                    </InputGroup>
                    {fieldState.invalid && (
                      <div className="flex items-center gap-1 mt-1" id="email-error">
                        <InfoIcon className="inline size-3 text-danger-200" />
                        <FieldError className="leading-4" errors={[fieldState.error]} />
                      </div>
                    )}
                  </FieldContent>
                </Field>
              )}
            />
          </FieldGroup>

          <Button
            type="submit"
            size="lg"
            className="mt-2"
            disabled={sendResetEmailMutation.isPending}
          >
            {sendResetEmailMutation.isPending ? (
              <>
                <Loader className="animate-spin mr-2" />
                Sending...
              </>
            ) : (
              "Send Reset Link"
            )}
          </Button>
        </form>
      </section>
    </div>
  );
};

export default ForgotPasswordEmail;
